// The router's vocabulary and URL grammar: the rail modes, which mode each
// admin view belongs to and which build flag hides it, the dimensions a
// scope path can name, the tab encoding, and how the app reads its tab
// strips and its mode from the URL. It imports nothing, so the app shell,
// the tab model, the admin menu and the analytics layer, which sits
// underneath the API client, all read this one definition instead of
// keeping copies.

const lookup = (entries) => Object.freeze(Object.assign(Object.create(null), entries));

// The query params this module's grammar and the analytics layer read. The
// other params belong to the pages that own them; analytics sends every
// param as `url_<name>` whether or not it is listed here.
export const ROUTER_PARAM = Object.freeze({
    ACTIVE: "active",
    ADMIN_VIEW: "adminView",
    CANVAS: "cv",
    CHAT: "chat",
    DCOL: "dcol",
    DISCOVERY: "discovery",
    DTAB: "dtab",
    FLEET_GROUP: "fleetGroup",
    FLEET_VIEW: "fleetView",
    FLEET_SEARCH: "fleetSearch",
    FLEET_TYPE: "fleetType",
    FLEET_LOCATION: "fleetLocation",
    FLEET_EVIDENCE: "fleetEvidence",
    FLEET_SELECTED: "fleetSelected",
    NOTIFICATION_REQUEST: "notificationRequest",
    FINDING: "finding",
    FOCUS: "focus",
    GEN: "gen",
    GOVERNANCE_TAB: "governanceTab",
    HIST: "hist",
    IEVIDENCE: "ievidence",
    IFINDING: "ifinding",
    IGROUP: "igroup",
    INVESTIGATING: "investigating",
    INVESTIGATION: "investigation",
    INVESTIGATION_PANEL: "investigationPanel",
    INVESTIGATION_STATUS: "investigationStatus",
    ISUBJECT: "isubject",
    LEGACY_CANVAS: "canvas",
    LEGACY_KERNEL: "kernel",
    LENS: "lens",
    LOG: "log",
    MODE: "mode",
    ORD: "ord",
    PANEL: "panel",
    QUERY: "q",
    RANGE: "range",
    SCOPE: "scope",
    SECURITY_VIEW: "securityView",
    SETTING: "setting",
    SETTINGS_VIEW: "settingsView",
    STEERS: "steers",
    STUDIO_ACTIVE: "studioActive",
    STUDIO_TABS: "studioTabs",
    TAB: "tab",
    TABS: "tabs",
    TRACE_QUERY: "tq",
    TURN: "turn",
    TRIAGE_FINDING: "triageFinding",
    TRIAGE_SCOPE: "triageScope",
    UI: "ui",
    UTAB: "utab",
    VIEW: "view",
    XCOL: "xcol",
});

// Rail order.
export const MODE_IDS = Object.freeze(["overview", "triage", "explore", "workspace", "security", "endpoints", "governance", "dashboard", "settings"]);

// A `mode` value older links still carry, and the mode it stands for.
const LEGACY_MODE_ALIASES = lookup({ admin: "settings" });

// Every admin view, whether or not a feature gate shows it, with its mode.
// The admin menu adds labels, permissions and gates on top. The settings
// views are the pages Settings had before its groups: old links still name
// them, and lib/settingsModel.js turns each into an address.
export const ADMIN_VIEW_MODES = lookup({
    "security-overview": "security",
    discovery: "security",
    alerts: "security",
    triage: "triage",
    endpoints: "endpoints",
    "unassigned-endpoints": "settings",
    connect: "settings",
    "endpoint-config": "settings",
    rollouts: "settings",
    tokens: "settings",
    installers: "settings",
    // Agent policy left Settings for Governance; its old page ids open it.
    "agent-policy": "governance",
    "ai-services": "governance",
    users: "settings",
    directory: "settings",
    escalations: "settings",
    "data-sources": "settings",
    notifications: "settings",
    account: "settings",
    "chat-models": "settings",
    mcp: "settings",
    report: "settings",
});

// Settings' groups and the sections in each, by id, in the order a reader
// meets them. `?setting=` names a group or a section as "<group>" or
// "<group>/<section>". lib/settingsModel.js gives each its name, peek and
// gate; `account` is the reader's own settings, opened from their name.
export const SETTINGS_SECTIONS = Object.freeze({
    endpoints: Object.freeze(["install", "enrolled", "tokens", "installers", "updates", "every-endpoint", "browser-chats", "collectors"]),
    services: Object.freeze(["anthropic", "openai", "perplexity", "langsmith", "copilot", "vertex", "litellm"]),
    directory: Object.freeze(["directories", "users", "groups", "devices", "no-person"]),
    access: Object.freeze(["people", "visibility", "requests"]),
    notifications: Object.freeze(["destinations"]),
    "trace-review": Object.freeze(["standards", "norms", "intents"]),
    chat: Object.freeze(["model", "provider"]),
    feedback: Object.freeze(["report"]),
    account: Object.freeze(["sign-in", "theme", "inbox"]),
});

// Every address `?setting=` can hold: each group, then each section.
export const SETTING_ADDRESSES = Object.freeze([
    ...Object.keys(SETTINGS_SECTIONS),
    ...Object.entries(SETTINGS_SECTIONS).flatMap(([group, sections]) => sections.map((section) => `${group}/${section}`)),
]);

// The address the URL asks for, or null when it names none.
export function requestedSetting(searchParams) {
    const raw = searchParams.get(ROUTER_PARAM.SETTING);
    return raw && SETTING_ADDRESSES.includes(raw) ? raw : null;
}

const ADMIN_VIEW_FLAGS = lookup({
    "security-overview": "securityEnabled",
    discovery: "securityEnabled",
    alerts: "securityEnabled",
});

// Every dimension a scope path can name, whether or not the tenant's data
// makes it visible. lib/dimensionDefs.js holds each one's metadata.
export const DIMENSION_IDS = Object.freeze([
    "agents", "endpoints", "identities", "models", "clusters", "projects", "sessions", "tools",
    "mcp_servers", "files", "commands", "hosts", "urls", "skills", "memories", "signals",
]);

// The canvases the product ships, which `cv` names by slug.
// lib/builtins/builtinCanvases.js defines them; a tenant's own canvases have
// slugs the tenant chose.
export const BUILTIN_CANVAS_SLUGS = Object.freeze([
    "security-posture", "ai-usage-overview", "adoption-and-concentration", "shadow-ai", "tools-and-resources", "token-spend",
]);

// ── Tabs ─────────────────────────────────────────────────────────

export const TAB_KINDS = new Set(["scope", "influence", "settings"]);



// An Explore tab with no scope, the strip's overview.
const scopeOverviewTab = () => ({ kind: "scope", payload: "" });

// Mode is implied by tab kind so the rail / sidebar / page chrome all
// derive from a single source of truth.
export function modeForTab(tab) {
    if (!tab) return "dashboard";
    switch (tab.kind) {
        case "scope":     return "workspace";
        case "influence": return "workspace";
        case "settings": return "settings";
        default:         return "dashboard";
    }
}

export function encodeTab(tab) {
    if (!tab) return "scope:";
    const kind = TAB_KINDS.has(tab.kind) ? tab.kind : "scope";
    const payload = tab.payload || "";
    return `${kind}:${payload}`;
}

export function decodeTab(raw) {
    if (typeof raw !== "string") return scopeOverviewTab();
    const colon = raw.indexOf(":");
    if (colon < 0) {
        // Legacy single-payload form — treat as a scope tab.
        return { kind: "scope", payload: raw };
    }
    const kind = raw.slice(0, colon);
    const payload = raw.slice(colon + 1);
    if (["studio", "kernel", "canvas", "workflow", "trigger", "skills"].includes(kind)) return scopeOverviewTab();
    if (!TAB_KINDS.has(kind)) {
        // Unknown prefix — preserve as scope so unknown future kinds
        // don't lose state on round-trip through an older client.
        return { kind: "scope", payload: raw };
    }
    return { kind, payload };
}













export function makeSettingsTab(viewId) {
    return { kind: "settings", payload: String(viewId || "") };
}



function activeIndex(raw, length) {
    return Math.max(0, Math.min(length - 1, Number(raw || 0)));
}

// The Explore strip: scope tabs plus influence-trace tabs. Legacy encoding
// (unprefixed payloads are encoded scope paths) keeps existing deep links
// working, a missing `tabs` falls back to the legacy `scope`, and any other
// kind, left from the earlier unified strip, becomes the scope overview.
export function readExplorerStrip(searchParams) {
    const raw = searchParams.get(ROUTER_PARAM.TABS);
    let tabs;
    if (raw !== null) {
        tabs = raw.length ? raw.split(";").map(decodeTab) : [scopeOverviewTab()];
    } else {
        tabs = [{ kind: "scope", payload: searchParams.get(ROUTER_PARAM.SCOPE) || "" }];
    }
    tabs = tabs.map((t) => (t.kind === "scope" || t.kind === "influence") ? t : scopeOverviewTab());
    return { tabs, activeIdx: activeIndex(searchParams.get(ROUTER_PARAM.ACTIVE), tabs.length) };
}



// ── Mode ─────────────────────────────────────────────────────────

export function requestedAdminView(searchParams) {
    return searchParams.get(ROUTER_PARAM.ADMIN_VIEW) || searchParams.get(ROUTER_PARAM.SETTINGS_VIEW);
}

// Resolve current modes and keep old Admin and endpoint setup bookmarks
// attached to their settings page. Explicit destinations win over a
// remembered settings selection; retired Explore reviews and traces migrate.
// Settings' former AI policy group (`?setting=policy...`) opens Governance.
export function resolveMode(searchParams, { validModes, defaultMode, viewModes }) {
    const rawMode = searchParams.get(ROUTER_PARAM.MODE);
    const legacyView = searchParams.get(ROUTER_PARAM.VIEW);
    const adminView = requestedAdminView(searchParams);
    if (!rawMode && searchParams.has(ROUTER_PARAM.CHAT) && validModes.has("dashboard")) return "dashboard";
    if (/^policy(\/|$)/.test(searchParams.get(ROUTER_PARAM.SETTING) || "") && validModes.has("governance")) return "governance";
    if ((!rawMode || rawMode === "explore") && legacyView === "scope.reviews" && validModes.has("triage")) return "triage";
    if ((!rawMode || rawMode === "explore") && /^trace:[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(searchParams.get(ROUTER_PARAM.TAB) || "") && validModes.has("workspace")) return "workspace";
    const requested = adminView || legacyView;
    const routed = rawMode === "settings" && viewModes[requested] ? viewModes[requested]
        : rawMode === "endpoints" && viewModes[requested] === "settings" ? "settings" : rawMode;
    if (validModes.has(routed)) return routed;
    if (LEGACY_MODE_ALIASES[rawMode]) return viewModes[adminView] || LEGACY_MODE_ALIASES[rawMode];
    if (legacyView && viewModes[legacyView]) return viewModes[legacyView];
    return defaultMode;
}

// The admin view the URL asks for in `mode`, or null: the requested admin
// view when it belongs to the mode, else the legacy `view` when it does.
export function requestedViewFor(searchParams, mode, viewModes) {
    const adminView = requestedAdminView(searchParams);
    if (adminView && viewModes[adminView] === mode) return adminView;
    const legacyView = searchParams.get(ROUTER_PARAM.VIEW);
    if (mode === "triage" && legacyView === "scope.reviews") return "triage";
    if (legacyView && viewModes[legacyView] === mode) return legacyView;
    return null;
}

// ── Scope paths ──────────────────────────────────────────────────

// The steps of an encoded scope path, `dim:ids/dim:ids`, as the dimension
// and its still-encoded ids. lib/dimensionDefs.js decodes the ids; the
// analytics layer reads only the dimensions.
export function scopeSegments(path) {
    if (!path) return [];
    const segments = [];
    for (const segment of path.split("/")) {
        const i = segment.indexOf(":");
        if (i === -1) continue;
        segments.push({ dim: segment.slice(0, i), rest: segment.slice(i + 1) });
    }
    return segments;
}

// The admin views a build shows, each with its mode: every view whose flag,
// if it has one, is on. It has no prototype, so a view name read from the
// URL, such as `toString`, finds nothing.
export function shownViewModes({ securityEnabled }) {
    const flags = { securityEnabled };
    return lookup(Object.fromEntries(
        Object.entries(ADMIN_VIEW_MODES).filter(([id]) => !ADMIN_VIEW_FLAGS[id] || flags[ADMIN_VIEW_FLAGS[id]]),
    ));
}

// Security is shown only with its existing tab flag enabled.
export function shownModes({ securityEnabled }) {
    return new Set(MODE_IDS.filter((id) => id !== "security" || securityEnabled));
}
